/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import TaxPage from "./TaxPage";
import {
  AdditionalPaymentRow,
  DECL_TABS,
  DeclarationSnapshot,
  DeductionRow,
  EditTabKey,
  LetOutProperty,
  OTHER_SOURCE_LABELS,
  OTHER_SOURCES_INCOME_KEYS,
  OTHER_SOURCES_TDS_KEYS,
  PaymentUnit,
  PossessionStatus,
  emptyOtherSources,
  fetchTaxDeclaration,
  formatAmount,
  getLoggedInEmpId,
  makeAdditionalPaymentRow,
  makeLetOutProperty,
  saveTaxDeclaration,
  toNumber,
} from "./taxSimplifier.service";

function CurrencyInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="tss-currency">
      <span>₹</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} inputMode="decimal" />
    </div>
  );
}

function PossessionToggle({
  value,
  onChange,
}: {
  value: PossessionStatus;
  onChange: (value: PossessionStatus) => void;
}) {
  const options: { key: PossessionStatus; label: string }[] = [
    { key: "yes", label: "Yes" },
    { key: "no", label: "No" },
  ];
  return (
    <div className="tss-chips">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          className={value === option.key ? "active" : ""}
          onClick={() => onChange(value === option.key ? "" : option.key)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function UnitToggle({
  value,
  onChange,
}: {
  value: PaymentUnit;
  onChange: (value: PaymentUnit) => void;
}) {
  const options: { key: PaymentUnit; label: string }[] = [
    { key: "INR", label: "INR" },
    { key: "PERCENT", label: "%" },
  ];
  return (
    <div className="tss-chips">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          className={value === option.key ? "active" : ""}
          onClick={() => onChange(option.key)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

const TaxDeclarationEdit: React.FC = () => {
  const navigate = useNavigate();
  const empId = useMemo(() => getLoggedInEmpId(), []);
  const [activeTab, setActiveTab] = useState<EditTabKey>("deductions");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState<DeclarationSnapshot | null>(null);
  const [totalIncomeDeductions, setTotalIncomeDeductions] = useState<DeductionRow[]>([]);
  const [savingsInvestments, setSavingsInvestments] = useState<DeductionRow[]>([]);
  const [homeLoanInterestPaid, setHomeLoanInterestPaid] = useState("0.00");
  const [eligibleForHousingLoanDeduction, setEligibleForHousingLoanDeduction] = useState(false);
  const [selfOccupiedPossession, setSelfOccupiedPossession] = useState<PossessionStatus>("");
  const [letOutProperties, setLetOutProperties] = useState<LetOutProperty[]>([]);
  const [otherSourcesValues, setOtherSourcesValues] = useState<Record<string, string>>(emptyOtherSources());
  const [additionalPayments, setAdditionalPayments] = useState<AdditionalPaymentRow[]>([]);

  const applyDeclaration = (next: DeclarationSnapshot) => {
    setTotalIncomeDeductions(next.totalIncomeDeductions);
    setSavingsInvestments(next.savingsInvestments);
    setHomeLoanInterestPaid(next.homeLoanInterestPaid);
    setEligibleForHousingLoanDeduction(next.eligibleForHousingLoanDeduction);
    setSelfOccupiedPossession(next.selfOccupiedPossession);
    setLetOutProperties(next.letOutProperties);
    setOtherSourcesValues(next.otherSourcesValues);
    setAdditionalPayments(next.additionalPayments);
  };

  useEffect(() => {
    const load = async () => {
      if (!empId) {
        toast.error("Employee ID not found");
        setLoading(false);
        return;
      }
      try {
        const next = await fetchTaxDeclaration(empId);
        setSnapshot(next);
        applyDeclaration(next);
      } catch (err: any) {
        toast.error(err?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [empId]);

  const currentSnapshot = (): DeclarationSnapshot => ({
    totalIncomeDeductions,
    savingsInvestments,
    homeLoanInterestPaid,
    eligibleForHousingLoanDeduction,
    selfOccupiedPossession,
    letOutProperties,
    otherSourcesValues,
    additionalPayments,
  });

  const totalDeductionsDeclared =
    totalIncomeDeductions.reduce((sum, row) => sum + toNumber(row.contributionAmount), 0) +
    savingsInvestments.reduce((sum, row) => sum + toNumber(row.contributionAmount), 0);
  const totalOtherIncomeDeclared = OTHER_SOURCES_INCOME_KEYS.reduce(
    (sum, key) => sum + toNumber(otherSourcesValues[key]),
    0
  );

  const handleConfirm = async () => {
    if (!empId) return;
    setSaving(true);
    try {
      const result = await saveTaxDeclaration(empId, currentSnapshot());
      toast.success(result.message || "Changes saved successfully");
      navigate(all_routes.taxSimplifierDetails, {
        state: { declarationSnapshot: result.snapshot },
      });
    } catch (err: any) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <TaxPage crumb="Edit Declarations">
      <button type="button" className="btn btn-link px-0 mb-2" onClick={() => navigate(all_routes.taxSimplifierDetails)}>
        <i className="ti ti-arrow-left me-1" />
        Back
      </button>
      <p className="tss-subtitle">Review your declarations and confirm before the deadline</p>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="text-muted mt-3 mb-0">Loading declarations</p>
        </div>
      ) : (
        <>
          <div className="tss-summary">
            <div>
              <div className="tss-summary-caption">Total Deductions Declared</div>
              <div className="tss-summary-value">
                <em>₹</em>
                {formatAmount(totalDeductionsDeclared)}
              </div>
            </div>
            <div style={{ background: "#e5e7eb" }} />
            <div>
              <div className="tss-summary-caption">Total Other Income Declared</div>
              <div className="tss-summary-value">
                <em>₹</em>
                {formatAmount(totalOtherIncomeDeclared)}
              </div>
            </div>
          </div>

          <div className="tss-tabs">
            {DECL_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={activeTab === tab.key ? "active" : ""}
                onClick={() => setActiveTab(tab.key)}
              >
                <i className={`ti ${tab.icon} me-1`} />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "deductions" &&
            (totalIncomeDeductions.length === 0 ? (
              <div className="tss-empty">No data available</div>
            ) : (
              totalIncomeDeductions.map((row) => (
                <div className="tss-card" key={row.id}>
                  <div className="fw-semibold">{row.label}</div>
                  <div className="tss-stat-row">
                    <div className="tss-stat-cell">
                      <div className="tss-stat-caption">Deduction Limit</div>
                      <div className="fw-semibold">{formatAmount(row.deductibleAmount)}</div>
                    </div>
                    {row.exemptionPercentage !== undefined && (
                      <div className="tss-stat-cell">
                        <div className="tss-stat-caption">Exemption %</div>
                        <div className="fw-semibold">{row.exemptionPercentage}%</div>
                      </div>
                    )}
                  </div>
                  <hr className="my-2" />
                  <div className="tss-field">
                    <label>Contribution Amount</label>
                    <CurrencyInput
                      value={row.contributionAmount}
                      onChange={(value) =>
                        setTotalIncomeDeductions((prev) =>
                          prev.map((item) => (item.id === row.id ? { ...item, contributionAmount: value } : item))
                        )
                      }
                    />
                  </div>
                </div>
              ))
            ))}

          {activeTab === "savingsInvestments" &&
            (savingsInvestments.length === 0 ? (
              <div className="tss-empty">No data available</div>
            ) : (
              savingsInvestments.map((row) => (
                <div className="tss-card" key={row.id}>
                  <div className="fw-semibold">{row.label}</div>
                  <div className="tss-stat-row">
                    <div className="tss-stat-cell">
                      <div className="tss-stat-caption">Deduction Limit</div>
                      <div className="fw-semibold">{formatAmount(row.deductibleAmount)}</div>
                    </div>
                  </div>
                  <hr className="my-2" />
                  <div className="tss-field">
                    <label>Contribution Amount</label>
                    <CurrencyInput
                      value={row.contributionAmount}
                      onChange={(value) =>
                        setSavingsInvestments((prev) =>
                          prev.map((item) => (item.id === row.id ? { ...item, contributionAmount: value } : item))
                        )
                      }
                    />
                  </div>
                </div>
              ))
            ))}

          {activeTab === "houseProperty" && (
            <>
              <div className="tss-card">
                <div className="tss-card-title">
                  <span className="tss-accent" />
                  Loss from Self Occupied House Property
                </div>
                <div className="tss-field">
                  <label>Home Loan Interest Paid</label>
                  <CurrencyInput value={homeLoanInterestPaid} onChange={setHomeLoanInterestPaid} />
                </div>
                <div className="tss-field">
                  <label>Eligible for Housing Loan Interest Deduction</label>
                  <div className="form-check form-switch m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={eligibleForHousingLoanDeduction}
                      onChange={(e) => setEligibleForHousingLoanDeduction(e.target.checked)}
                    />
                  </div>
                </div>
                <div className="tss-field">
                  <label>Possession Obtained</label>
                  <PossessionToggle value={selfOccupiedPossession} onChange={setSelfOccupiedPossession} />
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">Let-out Properties</div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setLetOutProperties((prev) => [...prev, makeLetOutProperty(prev.length + 1)])}
                >
                  <i className="ti ti-plus me-1" />
                  Add Property
                </button>
              </div>
              {letOutProperties.length === 0 ? (
                <div className="tss-empty">No let-out properties added yet. Tap &quot;Add Property&quot; to add one.</div>
              ) : (
                letOutProperties.map((property, index) => (
                  <div className="tss-card" key={property.id}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="fw-semibold">Property {index + 1}</div>
                      <button
                        type="button"
                        className="btn btn-link text-danger p-0"
                        onClick={() => setLetOutProperties((prev) => prev.filter((item) => item.id !== property.id))}
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                    {(
                      [
                        ["rentalIncome", "Rental Income (INR)"],
                        ["interestPaid", "Interest Paid (INR)"],
                        ["tdsOnRent", "TDS on Rent (INR)"],
                        ["repairExpenditure", "Repair Expenditure (INR)"],
                        ["otherExpenses", "Other Expenses (INR)"],
                      ] as const
                    ).map(([key, label]) => (
                      <div className="tss-field" key={key}>
                        <label>{label}</label>
                        <CurrencyInput
                          value={property[key]}
                          onChange={(value) =>
                            setLetOutProperties((prev) =>
                              prev.map((item) => (item.id === property.id ? { ...item, [key]: value } : item))
                            )
                          }
                        />
                      </div>
                    ))}
                    <div className="tss-field">
                      <label>Eligible for Housing Loan Interest Deduction</label>
                      <div className="form-check form-switch m-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={property.eligibleForDeduction}
                          onChange={(e) =>
                            setLetOutProperties((prev) =>
                              prev.map((item) =>
                                item.id === property.id ? { ...item, eligibleForDeduction: e.target.checked } : item
                              )
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="tss-field">
                      <label>Possession Obtained</label>
                      <PossessionToggle
                        value={property.possessionObtained}
                        onChange={(value) =>
                          setLetOutProperties((prev) =>
                            prev.map((item) =>
                              item.id === property.id ? { ...item, possessionObtained: value } : item
                            )
                          )
                        }
                      />
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === "otherSources" && (
            <>
              <div className="tss-card">
                <div className="tss-card-title">
                  <span className="tss-accent" />
                  Income from Other Sources
                </div>
                {OTHER_SOURCES_INCOME_KEYS.map((key) => (
                  <div className="tss-field" key={key}>
                    <label>{OTHER_SOURCE_LABELS[key]}</label>
                    <CurrencyInput
                      value={otherSourcesValues[key]}
                      onChange={(value) => setOtherSourcesValues((prev) => ({ ...prev, [key]: value }))}
                    />
                  </div>
                ))}
              </div>
              <div className="tss-card">
                <div className="tss-card-title">
                  <span className="tss-accent" />
                  TDS/TCS on Other Income
                </div>
                {OTHER_SOURCES_TDS_KEYS.map((key) => (
                  <div className="tss-field" key={key}>
                    <label>{OTHER_SOURCE_LABELS[key]}</label>
                    <CurrencyInput
                      value={otherSourcesValues[key]}
                      onChange={(value) => setOtherSourcesValues((prev) => ({ ...prev, [key]: value }))}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === "additional" && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">Additional Payments / Deductions</div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-link text-muted p-0" onClick={() => setAdditionalPayments([])}>
                    Reset
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => setAdditionalPayments((prev) => [...prev, makeAdditionalPaymentRow()])}
                  >
                    <i className="ti ti-plus me-1" />
                    Add
                  </button>
                </div>
              </div>
              {additionalPayments.length === 0 ? (
                <div className="tss-empty">No additional payments or deductions added yet</div>
              ) : (
                additionalPayments.map((payment, index) => (
                  <div className="tss-card" key={payment.id}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="fw-semibold">Additional Payment {index + 1}</div>
                      <button
                        type="button"
                        className="btn btn-link text-danger p-0"
                        onClick={() => setAdditionalPayments((prev) => prev.filter((item) => item.id !== payment.id))}
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                    <div className="tss-field">
                      <label>Additional Payment / Deduction</label>
                      <input
                        className="tss-type-input"
                        value={payment.type}
                        placeholder="e.g. Special Allowance"
                        onChange={(e) =>
                          setAdditionalPayments((prev) =>
                            prev.map((item) => (item.id === payment.id ? { ...item, type: e.target.value } : item))
                          )
                        }
                      />
                    </div>
                    <div className="tss-field">
                      <label>Value</label>
                      <CurrencyInput
                        value={payment.value}
                        onChange={(value) =>
                          setAdditionalPayments((prev) =>
                            prev.map((item) => (item.id === payment.id ? { ...item, value } : item))
                          )
                        }
                      />
                    </div>
                    <div className="tss-field">
                      <label>Unit</label>
                      <UnitToggle
                        value={payment.unit}
                        onChange={(unit) =>
                          setAdditionalPayments((prev) =>
                            prev.map((item) => (item.id === payment.id ? { ...item, unit } : item))
                          )
                        }
                      />
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          <div className="tss-bar">
            <button type="button" className="btn btn-light" disabled={saving} onClick={() => snapshot && applyDeclaration(snapshot)}>
              Revert
            </button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={handleConfirm}>
              {saving ? "Saving..." : "Confirm Changes"}
            </button>
          </div>
        </>
      )}
    </TaxPage>
  );
};

export default TaxDeclarationEdit;
